/* Copyright (c) 2018 Backpack Health LLC */

import React from 'react';
import * as _ from 'lodash';
import { FormattedMessage } from 'react-intl';

import Card, { CardSection, GrayCard } from '../../../components/card/Card';
import PreformattedText from '../../../components/PreformattedText';
import { ActionItemViewName, ActionItem } from '../../../../models/ActionItemModel';
import Spacer from '../../../components/Spacer';
import ProfilePhrases from '../../../../phraseKeys/Profile.phrase';
import { TextList } from '../../../components/List';
import { ScrollAwareScroller } from '../../../components/ScrollAware';
import { AppHeaderScrollableText } from '../../../components/appNav/AppHeaderScrollable';
import ContentWrapper from '../../../components/ContentWrapper';
import { ExternalOrInBrowserlLink } from '../../../components/TextLink';
import ProfileModel, { ProfileData } from '../../../../models/ProfileModel';

interface Props {
  title: {
    id: string;
    values?: object;
  };
  isNewProfile: boolean;
  profileActionItems: ActionItem[];
  profileData: ProfileData;
}

const actionItemsKeys = {
  [ActionItemViewName.policy_personal_info]: 'profileIntro.step.profilePrivacy.text',
  [ActionItemViewName.profile_name]: 'profile.name.field.label',
  [ActionItemViewName.profile_dob]: 'profile.DOB.field.label',
  [ActionItemViewName.profile_gender]: 'profile.gender.field.label',
  [ActionItemViewName.profile_residence]: 'profile.countrystate.field.label',
  [ActionItemViewName.policy_health_info]: 'profileIntro.step.healthInfoPrivacy.text',
  [ActionItemViewName.policy_dependent_authorization]: 'profileIntro.step.dependentAuth.text',
  [ActionItemViewName.policy_group_info]: 'profileIntro.step.profileGroupConsent.text',
};

function getActionItemKey(key) {
  return actionItemsKeys[key] || key;
}

const ActionItemListItem = ({ phraseId }) => (
  <li className="util-flex-start-center">
    <div className="bkgd-color-profile-highlight" style={{ borderRadius: 4, height: 8, width: 8 }} />
    <FormattedMessage id={phraseId}/>
  </li>
);

export class ProfileSetupIntro extends React.Component<Props, {}> {
  render() {
    const { isNewProfile, profileActionItems, title, profileData } = this.props;
    const profile = new ProfileModel(profileData);

    const isDependentProfile = profile.isDependent();
    const profileName = profile ? profile.greetingName() : null;
    const phraseMessages = isDependentProfile ? ProfilePhrases.dependentProfileSetupIntro(isNewProfile, profileName) : ProfilePhrases.profileSetupIntro(isNewProfile);

    const actionItems = _.groupBy(profileActionItems.sort(a => a.view === ActionItemViewName.policy_dependent_authorization ? -1 : 1), (p) => p.type);
    const dataElementActionItems = actionItems.data_element || [];
    const policyActionItems = actionItems.policy || [];

    return (
      <ScrollAwareScroller>
        <AppHeaderScrollableText {...title} />

        <ContentWrapper>

          <Card>
            <div>
              {
                dataElementActionItems.length > 0 && (
                  <CardSection className="font-body" hasBorder>
                    <PreformattedText>
                      <FormattedMessage id="profileIntro.dataSection.heading.text" />
                    </PreformattedText>

                    <TextList style={{ marginTop: 12 }}>
                      {dataElementActionItems.map((item, idx) => <ActionItemListItem key={idx} phraseId={getActionItemKey(item.view)} />)}
                    </TextList>
                  </CardSection>
                )
              }

              {
                policyActionItems.length > 0 && (
                  <CardSection className="font-body" hasBorder>
                    <PreformattedText>
                      <FormattedMessage id="profileIntro.policySection.heading.text" />
                    </PreformattedText>

                    <TextList style={{ marginTop: 12 }}>
                      {policyActionItems.map((item, idx) => <ActionItemListItem key={idx} phraseId={getActionItemKey(item.view)} />)}
                    </TextList>
                  </CardSection>
                )
              }
            </div>
          </Card>
          {
            isNewProfile ? (
              <GrayCard>
                <CardSection hasBorder={false}>
                  <h2 className="font-headline font-color-profile">
                    <FormattedMessage {...phraseMessages.helpHeading} />
                  </h2>
                  <Spacer size="5" />
                  <div className="font-footnote">
                    <FormattedMessage {...phraseMessages.help} />

                    {
                      isDependentProfile ? (
                        <FormattedMessage id="profile.newNotUser.helpMessage.link.URL">
                          {
                            (url: string) => (
                              <ExternalOrInBrowserlLink url={url} className="font-color-profile">
                                <FormattedMessage id="profile.newNotUser.helpMessage.link.label" />
                              </ExternalOrInBrowserlLink>
                            )
                          }
                        </FormattedMessage>
                      ) : null
                    }
                  </div>
                </CardSection>
              </GrayCard>
            ) : (
              <GrayCard>
                <CardSection hasBorder={false}>
                  <PreformattedText>
                    <div className="font-footnote font-color-gsMedDark">
                      <FormattedMessage {...phraseMessages.existingUserHelp} />
                    </div>
                  </PreformattedText>
                </CardSection>
              </GrayCard>
            )
          }

        </ContentWrapper>

      </ScrollAwareScroller>
    );
  }
}
